import { API_ENDPOINTS } from '../config/apiConfig';

/**
 * Integrity reports (#06) and live review sessions (#07) for the teacher console.
 *
 * Both are entitlement-gated, so a 402 is a normal answer meaning "this school has
 * not bought it" — distinct from a failure, and rendered as an upsell rather than
 * an error. Collapsing the two would tell a teacher the product is broken when it
 * is merely unpurchased.
 */

const headers = (): HeadersInit => {
  const h: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

// ── #06 integrity ────────────────────────────────────────────────────────────

export interface IntegrityEventSummary {
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  count: number;
}

export interface IntegrityReport {
  sessionId: string;
  studentName: string;
  integrityScore: number;
  band: string;
  eventCount: number;
  byType: IntegrityEventSummary[];
  /** True when the events behind this report were seeded, not observed. Must be shown. */
  syntheticData: boolean;
  note?: string;
}

export type IntegrityResult =
  | { state: 'ready'; report: IntegrityReport }
  | { state: 'locked' | 'forbidden' | 'error'; message?: string };

export const fetchIntegrityReport = async (sessionId: string): Promise<IntegrityResult> => {
  try {
    const response = await fetch(
      `${API_ENDPOINTS.learningApi}/sessions/${sessionId}/integrity`,
      { headers: headers() },
    );
    if (response.status === 402) return { state: 'locked' };
    if (response.status === 403) return { state: 'forbidden' };
    if (!response.ok) return { state: 'error', message: `Report unavailable (${response.status}).` };
    const body = await response.json();
    return { state: 'ready', report: body?.data ?? body };
  } catch {
    return { state: 'error', message: 'Could not reach the integrity service.' };
  }
};

// ── #07 live sessions ────────────────────────────────────────────────────────

export interface LiveSession {
  id: string;
  title: string;
  description?: string;
  teacherName?: string;
  packageId?: string;
  startsAt: string;
  durationMinutes?: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  capacity?: number | null;
  enrolledCount: number;
  placesLeft?: number | null;
}

export type LiveListResult =
  | { state: 'ready'; sessions: LiveSession[] }
  | { state: 'locked' | 'forbidden' | 'error'; message?: string };

export const fetchLiveSessions = async (): Promise<LiveListResult> => {
  try {
    const response = await fetch(`${API_ENDPOINTS.learningApi}/live`, { headers: headers() });
    if (response.status === 402) return { state: 'locked' };
    if (response.status === 403) return { state: 'forbidden' };
    if (!response.ok) return { state: 'error', message: `Could not load sessions (${response.status}).` };
    const body = await response.json();
    const data = body?.data ?? body;
    return { state: 'ready', sessions: Array.isArray(data) ? data : [] };
  } catch {
    return { state: 'error', message: 'Could not reach the live-session service.' };
  }
};

export interface NewLiveSession {
  title: string;
  description?: string;
  packageId?: string;
  startsAt: string;
  durationMinutes: number;
  capacity: number;
  joinUrl?: string;
}

/** @returns null on success, or the server's reason for refusing. */
export const scheduleLiveSession = async (session: NewLiveSession): Promise<string | null> => {
  try {
    const response = await fetch(`${API_ENDPOINTS.learningApi}/live`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(session),
    });
    if (response.ok) return null;
    if (response.status === 402) return 'Live sessions are not part of this plan.';
    if (response.status === 403) return 'Only an administrator can schedule a live session.';
    const body = await response.json().catch(() => ({}));
    return body?.message || `Could not schedule (${response.status}).`;
  } catch {
    return 'Could not reach the live-session service.';
  }
};
